import { GridColDef, GridExpandMoreIcon } from "@mui/x-data-grid";

import { CircularProgress, Grid2 } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Accordion,
  AccordionSummary,
  Button,
  GKClientDataGrid,
  IconButton,
  StatusField,
  Typography,
} from "components";
import dayjs from "dayjs";
import { useState } from "react";
import toast from "react-hot-toast";
import { IoIosSend } from "react-icons/io";
import { MdOutlineFileDownload } from "react-icons/md";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField, handleDownloadFile } from "utils/helpers";
import BillingModal from "./BillingModal";

interface Props {
  revenueData: any;
  revenueRefetch: () => void;
}

const BillingDetails = (props: Props) => {
  const { revenueData, revenueRefetch } = props;

  const { id } = useParams();

  const [openCreateModule, setOpenCreateModule] = useState(false);

  const [openModule, setOpenModule] = useState({});

  const [sendingId, setSendingId] = useState<string | null>(null);

  const {
    data: billingDetails,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["GET_BILLING_DETAILS"],
    queryFn: () => axiosInstance.get(`/super_admin/${id}/get-billing-details/`),
  });

  const { mutate: handleSendInvoice } = useMutation({
    mutationKey: ["SEND_INVOICE"],
    mutationFn: (data: any) =>
      axiosInstance.post(`/super_admin/${id}/send-invoice/`, data),
    onSuccess: (response) => {
      toast.success(response?.data?.message || "Message Sent Successfully");
      setSendingId(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
      setSendingId(null);
    },
  });

  const billingDetailsColumns: GridColDef[] = [
    {
      ...extraColumnsField("AMC Charges", "amcCharges", 150, 1),
    },
    {
      ...extraColumnsField("Portfolio Count", "portfolioCount", 150, 1),
    },
    {
      ...extraColumnsField("Date", "billingDate", 150, 1),
      valueGetter: (row) => dayjs(row?.row?.billingDate).format("DD/MM/YYYY"),
    },
    {
      ...extraColumnsField("Status", "status", 150, 1),
      renderCell: (params) => (
        <StatusField statusString={params?.row?.status} />
      ),
    },
    {
      ...extraColumnsField("Action", "action", 100, 1),
      headerAlign: "right",
      align: "right",
      renderCell: (params) => (
        <Grid2 container gap={1} justifyContent={"flex-end"}>
          <IconButton
            onClick={(event) => {
              event?.stopPropagation();
              handleDownloadFile({
                link: params?.row?.invoiceDetails?.invoicePdf,
                name: params?.row?.invoiceDetails?.invoiceName,
              });
            }}
            disabled={
              !params.row.generateInvoice ||
              !params?.row?.invoiceDetails?.invoicePdf
            }
          >
            <MdOutlineFileDownload />
          </IconButton>
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              setSendingId(params.row.id);
              handleSendInvoice({ billingId: params.row.id });
            }}
            disabled={
              !params.row.generateInvoice || sendingId === params.row.id
            }
          >
            {sendingId === params.row.id ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <IoIosSend />
            )}
          </IconButton>
        </Grid2>
      ),
    },
  ];

  return (
    <>
      <Accordion
        disableGutters
        style={{
          border: `solid 1px var(--neutral-100)`,
          borderRadius: "8px",
          boxShadow: "none",
          background: "none",
        }}
      >
        <AccordionSummary
          expandIcon={<GridExpandMoreIcon />}
          sx={{
            "& .MuiAccordionSummary-content": {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            },
          }}
        >
          <Typography
            fontSize={16}
            fontWeight={600}
            textTransform={"none"}
            color={"var(--primary-900)"}
          >
            Billing Details
          </Typography>
        </AccordionSummary>
        <GKClientDataGrid
          loading={isLoading}
          columns={billingDetailsColumns}
          rows={billingDetails?.data?.data}
          headerComponent={
            <Button
              variant="contained"
              size="medium"
              onClick={() => {
                setOpenModule({});
                setOpenCreateModule(true);
              }}
            >
              Create Billing
            </Button>
          }
          showFiltersButton={false}
          onRowClick={(row) => {
            setOpenModule(row.row);
            setOpenCreateModule(true);
          }}
        />
      </Accordion>

      {openCreateModule && (
        <BillingModal
          inModuleOpen={openCreateModule}
          setIsModuleOpen={setOpenCreateModule}
          openModule={openModule}
          refetch={refetch}
          revenueData={revenueData}
          revenueRefetch={revenueRefetch}
        />
      )}
    </>
  );
};

export default BillingDetails;
